import { MeasurementSystem } from "velavu-js-api";

//Converts a speed from a speed unit to its measurement system
export function speedUnitToMeasurementSystem(
	speed: "mph" | "km/h",
): MeasurementSystem {
	if (speed === "mph") {
		return MeasurementSystem.Imperial;
	} else {
		return MeasurementSystem.Metric;
	}
}

//Guesses what measurement system to use depending on the user's locale
export function getDefaultMeasurementSystem(): MeasurementSystem {
	//Determining the user's region
	let userRegion: string | undefined = undefined;
	if (navigator.languages) {
		//Checking the first language with a region
		for (const lang of navigator.languages) {
			const locale = new Intl.Locale(lang);
			if (locale.region !== undefined) {
				userRegion = locale.region;
				break;
			}
		}
	} else {
		userRegion = new Intl.Locale(navigator.language).region;
	}

	return userRegion === "US"
		? MeasurementSystem.Imperial
		: MeasurementSystem.Metric;
}

/**
 * Converts a speed from m/s into the specified unit
 * @param speed The speed in meters per second
 * @param unit The measurement system to use
 */
export function convertSpeed(speed: number, unit: MeasurementSystem): number {
	if (unit === MeasurementSystem.Imperial) {
		return speed * 2.237;
	} else {
		return speed * 3.6;
	}
}

/**
 * Converts a speed from m/s into the user's preferred unit
 * @param speed The speed in meters per second
 * @param unit The measurement system to use
 */
export function formatSpeed(speed: number, unit: MeasurementSystem): string {
	if (unit === MeasurementSystem.Imperial) {
		return Math.round(speed * 2.237) + " mph";
	} else {
		return Math.round(speed * 3.6) + " km/h";
	}
}

/**
 * Converts a speed from meters into the user's preferred unit
 * @param distance The speed in meter
 * @param unit The measurement system to use
 */
export function formatDistance(
	distance: number,
	unit: MeasurementSystem,
): string {
	if (unit === MeasurementSystem.Imperial) {
		return (distance / 1609).toFixed(1) + " mi";
	} else {
		return (distance / 1000).toFixed(1) + " km";
	}
}

/**
 * Formats a small distance in meters into the user's preferred unit
 * @param distance The distance in meters
 * @param unit The measurement system to use
 */
export function formatSmallDistance(distance: number, unit: MeasurementSystem) {
	if (unit === MeasurementSystem.Imperial) {
		return (distance / 3.281).toFixed(1) + " ft";
	} else {
		return distance.toFixed(1) + " m";
	}
}

/**
 * Returns the small distance unit name based on the measurement system
 * @param unit The name of the unit to use under the specified measurement system
 */
export function getSmallDistanceUnit(unit: MeasurementSystem) {
	if (unit === MeasurementSystem.Imperial) {
		return "ft";
	} else {
		return "m";
	}
}

/**
 * Converts a speed from meters into the specified unit
 * @param distance The speed in meter
 * @param unit The measurement system to use
 */
export function convertDistance(
	distance: number,
	unit: MeasurementSystem,
): number {
	if (unit === MeasurementSystem.Imperial) {
		return distance / 1609;
	} else {
		return distance / 1000;
	}
}

/**
 * Converts a small distance from meters into the specified unit
 * @param distance The distance in meters
 * @param unit The measurement system to use
 */
export function convertSmallDistance(
	distance: number,
	unit: MeasurementSystem,
) {
	if (unit === MeasurementSystem.Imperial) {
		return distance * 3.281;
	} else {
		return distance;
	}
}

/**
 * Formats a measured distance in meters for the map measure tool.
 * Short distances use feet or meters; longer distances use miles or kilometers.
 * @param distance The distance in meters
 * @param unit The measurement system to use
 */
export function formatMeasuredDistance(
	distance: number,
	unit: MeasurementSystem,
): string {
	switch (unit) {
		case MeasurementSystem.Imperial:
			const miles = convertDistance(distance, unit);
			if (miles < 0.1) {
				return convertSmallDistance(distance, unit).toFixed(1) + " ft";
			} else {
				return miles.toFixed(2) + " mi";
			}
		case MeasurementSystem.Metric:
			if (distance < 1000) {
				return distance.toFixed(1) + " m";
			} else {
				return (distance / 1000).toFixed(2) + " km";
			}
	}
}

function celciusToFahrenheit(temperature_c: number): number {
	return (temperature_c * 9) / 5 + 32;
}

/**
 * Formats a temperature reading based on specified unit
 * @param temperature_c The temperature in Celsius
 * @param unit The measurement system to use
 */
export function formatTemperature(
	temperature_c: number,
	unit: MeasurementSystem,
): number {
	if (unit === MeasurementSystem.Imperial) {
		return celciusToFahrenheit(temperature_c);
	} else {
		return temperature_c;
	}
}

/**
 * Formats a temperature reading to a string with the unit based on specified unit
 * @param temperature_c The temperature in Celsius
 * @param unit The measurement system to use
 */
export function formatTemperatureString(
	temperature_c: number,
	unit: MeasurementSystem,
): string {
	if (unit === MeasurementSystem.Imperial) {
		return Math.round(celciusToFahrenheit(temperature_c)) + "°F";
	} else {
		return Math.round(temperature_c) + "°C";
	}
}

/**
 * Converts a speed from a human-readable speed unit to meters per second
 * @param speed The speed value
 * @param unit The speed unit
 */
export function normalizeSpeed(speed: number, unit: MeasurementSystem) {
	if (unit === MeasurementSystem.Imperial) {
		return speed / 2.237;
	} else if (unit === MeasurementSystem.Metric) {
		return speed / 3.6;
	} else {
		throw new Error("Unknown speed unit " + unit);
	}
}
